import { z } from 'zod';
import { email, monthKey, password, role } from './primitives.js';
import { bank } from './banks.js';
import { screenKey } from './screens.js';
import { blocoKey } from './blocos.js';

/**
 * Contratos de autenticacao e de conta.
 *
 * Regra que atravessa todo este arquivo: nenhuma resposta pode revelar se um
 * e-mail existe. Login e "esqueci a senha" devolvem a mesma mensagem e o mesmo
 * custo de CPU nos dois casos — por isso as respostas aqui sao deliberadamente
 * pobres em informacao.
 */

export const loginRequest = z.object({
  email,
  /**
   * Sem `min` de propósito: no login, senha curta e senha errada devem ser
   * indistinguiveis. Validar tamanho aqui devolveria 400 em vez de 401 e
   * entregaria informacao sobre a politica de senha da conta.
   */
  password: z.string().max(200),
});
export type LoginRequest = z.infer<typeof loginRequest>;

/** Usuario da sessao corrente. Nunca carrega hash nem `token_version`. */
export const sessionUser = z.object({
  id: z.number().int().positive(),
  email: z.string(),
  name: z.string(),
  role,
  /** Caminho da foto em `/uploads`, ou null para quem nunca enviou. */
  avatar_path: z.string().nullable().default(null),
  /** Bancos que esta conta pode usar como contexto financeiro. */
  banks: z.array(bank).default([]),
  /** Telas liberadas. Admin recebe o catalogo inteiro, montado no servidor. */
  screens: z.array(screenKey).default([]),
  /** Blocos da Home liberados. Admin recebe o catalogo inteiro. */
  blocks: z.array(blocoKey).default([]),
  /**
   * Banco onde a conta pode lancar. `null` = somente leitura. Os bancos que ela
   * VE estao em `banks`, e as telas analiticas somam todos.
   */
  classification_bank_id: z.number().int().positive().nullable().default(null),
  /** Duas etapas ligadas. A tela de perfil usa para saber o que oferecer. */
  totp_ativo: z.boolean().default(false),
});
export type SessionUser = z.infer<typeof sessionUser>;

export const forgotPasswordRequest = z.object({ email });

export const resetPasswordRequest = z.object({
  token: z.string().min(1).max(200),
  password,
});

/** Troca de senha pelo proprio usuario logado. */
export const changePasswordRequest = z.object({
  current_password: z.string().min(1, 'Informe a senha atual.').max(200),
  new_password: password,
});

export const updateProfileRequest = z.object({
  name: z.string().trim().min(1, 'Nome e obrigatorio.').max(128),
});

/** Gestao de contas — restrito a `admin`. */
export const createUserRequest = z.object({
  email,
  password,
  name: z.string().trim().min(1, 'Nome e obrigatorio.').max(128),
  role: role.default('user'),
  bank_ids: z.array(z.number().int().positive()).min(1, 'Selecione ao menos um banco.'),
});

export const updateUserRequest = z
  .object({
    email,
    name: z.string().trim().min(1).max(128),
    role,
    bank_ids: z.array(z.number().int().positive()).min(1).optional(),
    /** Opcional: quando presente, derruba as sessoes ativas da conta. */
    password,
  })
  .partial()
  .refine((body) => Object.keys(body).length > 0, {
    message: 'Envie ao menos um campo para alterar.',
  });

export const user = z.object({
  id: z.number().int().positive(),
  email: z.string(),
  name: z.string(),
  role,
  avatar_path: z.string().nullable().default(null),
  banks: z.array(bank).default([]),
  created_at: z.string().nullable(),
  last_login_at: z.string().nullable(),
});
export type User = z.infer<typeof user>;

/** Link publico de compartilhamento do caixa. */
export const createShareRequest = z.object({
  page: z.enum(['caixa']).default('caixa'),
  periods: z.array(monthKey).min(1, 'Selecione ao menos uma competencia.'),
  /** Validade em dias. */
  days: z.number().int().min(1).max(365).default(7),
});

export const shareLink = z.object({
  id: z.number().int().positive(),
  token: z.string(),
  page: z.string(),
  periods: z.array(z.string()),
  expires_at: z.string(),
  created_at: z.string().nullable(),
});
export type ShareLink = z.infer<typeof shareLink>;
